import type { ChainId } from '@/stores/slices/chainSlice';
import type { ListType } from '@/types/api/lists';

export interface ChainConfig {
  id: ChainId;
  apiSlug: string;
  numericId: number;
  name: string;
  shortName: string;
  icon: string;
  color: string;
  nativeSymbol: string;
  listType: ListType;
  explorerUrl: string;
  /** Path segment for wallet/account pages on the explorer (e.g. "address" or "account") */
  explorerAccountPath: string;
  addressPattern: RegExp;
  dexscreenerId: string;
}

export const CHAINS: ChainConfig[] = [
  {
    id: 'bsc',
    apiSlug: 'bsc',
    numericId: 56,
    name: 'BNB Chain',
    shortName: 'BSC',
    icon: '/chains/bsc.svg',
    color: 'text-yellow-500',
    nativeSymbol: 'BNB',
    listType: 'BscAddress',
    explorerUrl: 'https://bscscan.com',
    explorerAccountPath: 'address',
    addressPattern: /^0x[a-fA-F0-9]{40}$/,
    dexscreenerId: 'bsc',
  },
  {
    id: 'base',
    apiSlug: 'base',
    numericId: 8453,
    name: 'Base',
    shortName: 'Base',
    icon: '/chains/base.webp',
    color: 'text-blue-500',
    nativeSymbol: 'ETH',
    listType: 'BaseAddress',
    explorerUrl: 'https://basescan.org',
    explorerAccountPath: 'address',
    addressPattern: /^0x[a-fA-F0-9]{40}$/,
    dexscreenerId: 'base',
  },
  {
    id: 'sol',
    apiSlug: 'solana',
    numericId: 501,
    name: 'Solana',
    shortName: 'SOL',
    icon: '/chains/solana.svg',
    color: 'text-purple-500',
    nativeSymbol: 'SOL',
    listType: 'SolAddress',
    explorerUrl: 'https://solscan.io',
    explorerAccountPath: 'account',
    addressPattern: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
    dexscreenerId: 'solana',
  },
];

export const CHAIN_MAP = Object.fromEntries(CHAINS.map((c) => [c.id, c])) as Record<ChainId, ChainConfig>;
export const CHAIN_API_SLUG_MAP = Object.fromEntries(
  CHAINS.map((c) => [c.apiSlug, c])
) as Partial<Record<string, ChainConfig>>;
export const CHAIN_NUMERIC_ID_MAP = Object.fromEntries(
  CHAINS.map((c) => [c.numericId, c])
) as Partial<Record<number, ChainConfig>>;

export const isChainId = (value: string | null | undefined): value is ChainId => {
  return value === 'bsc' || value === 'base' || value === 'sol';
};

export const getChainIdForListType = (listType: ListType): ChainId | null => {
  const chain = CHAINS.find((config) => config.listType === listType);
  return chain?.id ?? null;
};

/** All address list types (for filtering lists by address type) */
export const ADDRESS_LIST_TYPES: ListType[] = CHAINS.map((c) => c.listType);
